import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import {
  MAX_ADDON_TITLE_LENGTH,
  MAX_DISCOUNT_PCT,
  MAX_NAME_LENGTH,
  MAX_SESSIONS,
  MIN_DISCOUNT_PCT,
  MIN_SESSIONS,
  formatPackagePreview,
  computePackagePriceCents,
  formatHKD,
  validatePackageDraft,
} from './packagePricing.js';

/**
 * One package card: summary when collapsed, full editor when expanded.
 * Purely presentational — the parent owns the draft state and the save call.
 */
const PackageEditor = ({
  draft,
  basePriceCents,
  expanded,
  disabled,
  onToggleExpanded,
  onChange,
  onAddonChange,
  onAddonAdd,
  onAddonRemove,
  onAddonMove,
  onRemove,
}) => {
  const errors = validatePackageDraft(draft);
  const preview = formatPackagePreview(basePriceCents, draft.sessions, draft.discount_pct);
  const addons = draft.addons || [];
  // formatPackagePreview returns null for two very different reasons: the
  // therapist has no session price to compute from, or the draft's numbers are
  // outside the allowed ranges. Saying "add your session price" to someone who
  // has one and mistyped the sessions sends them to the wrong screen.
  const hasBasePrice = Number.isFinite(Number(basePriceCents)) && Number(basePriceCents) > 0;
  const previewHint = hasBasePrice
    ? `Sessions ${MIN_SESSIONS}-${MAX_SESSIONS} and discount ${MIN_DISCOUNT_PCT}-${MAX_DISCOUNT_PCT}% produce a package price.`
    : 'Add your regular session price in Manage Account and Profile to see package prices.';

  return (
    <Box
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '10px',
        backgroundColor: '#fff',
        p: 2,
        mb: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {draft.name || 'Untitled package'}
        </Typography>
        <Chip size="small" label={`${draft.sessions} session${Number(draft.sessions) === 1 ? '' : 's'}`} />
        <Chip size="small" label={`${draft.discount_pct}% off`} />
        {(draft._new || !draft.id) && <Chip size="small" label="Not saved yet" sx={{ backgroundColor: '#FFF4E5' }} />}
        {draft.id && !draft.active && <Chip size="small" label="Hidden from clients" />}
        <Box sx={{ flexGrow: 1 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={Boolean(draft.active)}
              disabled={disabled}
              onChange={(e) => onChange({ active: e.target.checked })}
            />
          }
          label={<Typography variant="body2">Active</Typography>}
        />
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onToggleExpanded}
          aria-label={expanded ? 'Finish editing package' : 'Edit package'}
          title={expanded ? 'Finish editing package' : 'Edit package'}
        >
          {expanded ? <DoneIcon /> : <EditIcon />}
        </IconButton>
      </Stack>

      <Box role={preview ? 'group' : undefined} aria-label={preview ? 'Package price' : undefined} sx={{ mt: 2, p: 1.5, backgroundColor: '#F4FAF9', borderRadius: '8px' }}>
        {preview ? <>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 600, color: '#167D79' }}>{formatHKD(computePackagePriceCents(basePriceCents, draft.sessions, draft.discount_pct))}</Typography>
          <Typography variant="body2" color="text.secondary">{Number(draft.discount_pct) > 0 ? <><Box component="span" sx={{ textDecoration: 'line-through', mr: 1 }}>{formatHKD(basePriceCents * Number(draft.sessions))}</Box>Save {formatHKD(basePriceCents * Number(draft.sessions) - computePackagePriceCents(basePriceCents, draft.sessions, draft.discount_pct))} ({draft.discount_pct}% off)</> : 'Standard session rate · No discount'}</Typography>
        </> : <Typography variant="body2" color="text.secondary">{previewHint}</Typography>}
      </Box>
      {onRemove && <Button size="small" disabled={disabled} onClick={onRemove} sx={{ mt: 1, textTransform: 'none' }}>Cancel new package</Button>}

      {expanded && (
        <Box sx={{ mt: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Package name"
              value={draft.name}
              disabled={disabled}
              onChange={(e) => onChange({ name: e.target.value })}
              error={Boolean(errors.name)}
              helperText={errors.name || `${(draft.name || '').trim().length}/${MAX_NAME_LENGTH}`}
              size="small"
              fullWidth
            />
            <TextField
              label="Sessions"
              type="number"
              value={draft.sessions}
              disabled={disabled}
              onChange={(e) => onChange({ sessions: e.target.value })}
              error={Boolean(errors.sessions)}
              helperText={errors.sessions || `${MIN_SESSIONS}-${MAX_SESSIONS}`}
              size="small"
              sx={{ width: { xs: '100%', sm: 140 } }}
              inputProps={{ min: MIN_SESSIONS, max: MAX_SESSIONS, step: 1 }}
            />
            <TextField
              label="Discount %"
              type="number"
              value={draft.discount_pct}
              disabled={disabled}
              onChange={(e) => onChange({ discount_pct: e.target.value })}
              error={Boolean(errors.discount_pct)}
              helperText={errors.discount_pct || `${MIN_DISCOUNT_PCT}-${MAX_DISCOUNT_PCT}`}
              size="small"
              sx={{ width: { xs: '100%', sm: 140 } }}
              inputProps={{ min: MIN_DISCOUNT_PCT, max: MAX_DISCOUNT_PCT, step: 1 }}
            />
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Typography variant="subtitle2">Package inclusions</Typography>
            <Typography variant="caption" color="text.secondary">
              Displayed on the package card — no extra charge, no extra session.
            </Typography>
          </Stack>

          {addons.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No inclusions yet.
            </Typography>
          )}

          {addons.map((addon, index) => (
            <Stack
              key={addon.key || `addon-${index}`}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems="flex-start"
              sx={{ mt: 1 }}
            >
              <TextField
                label="Title"
                value={addon.title}
                disabled={disabled}
                onChange={(e) => onAddonChange(index, { title: e.target.value })}
                error={Boolean(errors[`addon-${index}-title`])}
                helperText={
                  errors[`addon-${index}-title`] || `${(addon.title || '').trim().length}/${MAX_ADDON_TITLE_LENGTH}`
                }
                size="small"
                sx={{ flex: 1, width: { xs: '100%', md: 'auto' } }}
              />
              <TextField
                label="Description (optional)"
                value={addon.description || ''}
                disabled={disabled}
                onChange={(e) => onAddonChange(index, { description: e.target.value })}
                size="small"
                sx={{ flex: 2, width: { xs: '100%', md: 'auto' } }}
              />
              <Box sx={{ display: 'flex', gap: 0.5, alignSelf: { xs: 'flex-end', md: 'flex-start' } }}>
              <IconButton
                size="small"
                disabled={disabled || index === 0}
                onClick={() => onAddonMove(index, -1)}
                title="Move up"
                aria-label="Move up"
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={disabled || index === addons.length - 1}
                onClick={() => onAddonMove(index, 1)}
                title="Move down"
                aria-label="Move down"
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => onAddonRemove(index)}
                title="Remove inclusion"
                aria-label="Remove inclusion"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              </Box>
            </Stack>
          ))}

          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={onAddonAdd}
            sx={{ mt: 1, color: '#167D79', textTransform: 'none' }}
          >
            Add inclusion
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PackageEditor;
